import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const PROGRAM_ITEMS = [
  "All Programs",
  "Digital Money and Stablecoins",
  "Digital Asset Accounting",
  "Business Payments and Treasury",
  "Compliance for Digital Finance Operations",
  "Digital Asset Audit and Assurance",
  "Certification Pathways",
];

const PROGRAM_LINKS = {
  "All Programs": "/programs",
  "Digital Money and Stablecoins": "/programs/digital-money-and-stablecoins",
  "Digital Asset Accounting": "/programs/digital-asset-accounting",
  "Business Payments and Treasury": "/programs/business-payments-and-treasury",
  "Compliance for Digital Finance Operations": "/programs/compliance-for-digital-finance-operations",
  "Digital Asset Audit and Assurance": "/programs/digital-asset-audit-and-assurance",
  "Certification Pathways": "/certification-pathways",
};

const NAV_ITEMS = ["Resources", "Membership", "Partners", "About"];

const NAV_LINKS = {
  Resources: "/resources",
  Membership: "/membership",
  Partners: "/partners",
  About: "/about",
};

const inert = (e) => e.preventDefault();

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const { pathname } = useLocation();

  const isActive = (href) =>
    href && href !== "#"
      ? href === "/"
        ? pathname === "/"
        : pathname === href || pathname.startsWith(`${href}/`)
      : false;
  const programsActive =
    pathname.startsWith("/programs") || pathname === "/certification-pathways";
  const activeText = "text-[hsl(174_86%_24%)]";

  return (
    <header
      className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75"
      data-testid="site-header"
    >
      <div className="relative mx-auto flex h-16 w-full max-w-[1288px] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          data-testid="header-logo-link"
        >
          <img
            src="/wfii-logo.png"
            alt="World Financial Innovation Institute"
            className="h-11 w-auto sm:h-12"
            data-testid="header-logo-image"
          />
        </a>

        {/* Desktop navigation — centered in the container */}
        <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg:block">
          <NavigationMenu>
            <NavigationMenuList className="gap-0.5">
              <NavigationMenuItem>
                <NavigationMenuLink
                  href="/"
                  className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    isActive("/") ? activeText : "text-foreground"
                  }`}
                  data-testid="header-nav-home"
                >
                  Home
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className={`h-9 bg-transparent px-3 text-sm font-medium hover:bg-muted focus:bg-muted data-[state=open]:bg-muted data-[state=open]:hover:bg-muted data-[state=open]:focus:bg-muted ${
                    programsActive
                      ? "text-[hsl(174_86%_24%)] hover:text-[hsl(174_86%_24%)] focus:text-[hsl(174_86%_24%)] data-[state=open]:text-[hsl(174_86%_24%)]"
                      : "text-foreground hover:text-foreground focus:text-foreground data-[state=open]:text-foreground"
                  }`}
                  data-testid="header-programs-dropdown-trigger"
                >
                  Programs
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[340px] gap-1 p-3">
                    {PROGRAM_ITEMS.map((item, i) => (
                      <li key={item}>
                        {(i === 1 || i === PROGRAM_ITEMS.length - 1) && (
                          <div className="my-1.5 h-px bg-border" aria-hidden="true" />
                        )}
                        <NavigationMenuLink
                          href={PROGRAM_LINKS[item] || "#"}
                          onClick={PROGRAM_LINKS[item] ? undefined : inert}
                          className={`block rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            item === "All Programs" || item === "Certification Pathways"
                              ? "font-semibold"
                              : ""
                          } ${
                            pathname === PROGRAM_LINKS[item] ? activeText : "text-foreground"
                          }`}
                          data-testid={`header-programs-dropdown-item-${slug(item)}`}
                        >
                          {item}
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {NAV_ITEMS.map((item) => (
                <NavigationMenuItem key={item}>
                  <NavigationMenuLink
                    href={NAV_LINKS[item] || "#"}
                    onClick={NAV_LINKS[item] ? undefined : inert}
                    className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      isActive(NAV_LINKS[item]) ? activeText : "text-foreground"
                    }`}
                    data-testid={`header-nav-${slug(item)}`}
                  >
                    {item}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Desktop CTA — right side */}
        <Button
          asChild
          variant="outline"
          className="hidden h-9 border-border px-4 text-sm font-medium lg:inline-flex"
          data-testid="header-become-member-button"
        >
          <Link to="/enrollment-request" state={{ type: "membership" }}>
            Become a Member
          </Link>
        </Button>

        {/* Mobile navigation */}
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                data-testid="mobile-nav-open-button"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] overflow-y-auto">
              <SheetHeader className="text-left">
                <SheetTitle className="text-sm font-semibold tracking-tight">
                  World Financial Innovation Institute
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Site navigation menu
                </SheetDescription>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1" data-testid="mobile-nav">
                <a
                  href="/"
                  className={`rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted ${
                    isActive("/") ? activeText : "text-foreground"
                  }`}
                  data-testid="mobile-nav-home"
                >
                  Home
                </a>

                <Collapsible open={programsOpen} onOpenChange={setProgramsOpen}>
                  <CollapsibleTrigger
                    className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted ${
                      programsActive ? activeText : "text-foreground"
                    }`}
                    data-testid="mobile-nav-programs-collapsible-trigger"
                  >
                    Programs
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                        programsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-3 flex flex-col gap-0.5 pl-3 py-1">
                      {PROGRAM_ITEMS.map((item, i) => (
                        <div key={item}>
                          {(i === 1 || i === PROGRAM_ITEMS.length - 1) && (
                            <div className="my-1.5 h-px bg-border" aria-hidden="true" />
                          )}
                          <a
                            href={PROGRAM_LINKS[item] || "#"}
                            onClick={PROGRAM_LINKS[item] ? undefined : inert}
                            className={`block rounded-md px-3 py-2 text-sm hover:bg-muted ${
                              item === "All Programs" || item === "Certification Pathways"
                                ? "font-semibold"
                                : ""
                            } ${
                              pathname === PROGRAM_LINKS[item]
                                ? activeText
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`mobile-nav-programs-item-${slug(item)}`}
                          >
                            {item}
                          </a>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {NAV_ITEMS.map((item) => (
                  <a
                    key={item}
                    href={NAV_LINKS[item] || "#"}
                    onClick={NAV_LINKS[item] ? undefined : inert}
                    className={`rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted ${
                      isActive(NAV_LINKS[item]) ? activeText : "text-foreground"
                    }`}
                    data-testid={`mobile-nav-${slug(item)}`}
                  >
                    {item}
                  </a>
                ))}

                <Button
                  asChild
                  className="mt-3 h-11 w-full bg-[hsl(var(--accent))] text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
                  data-testid="mobile-nav-become-member"
                >
                  <Link to="/enrollment-request" state={{ type: "membership" }}>
                    Become a Member
                  </Link>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
